import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { stats, alertsApi, quotations } from '../utils/api';
import { StatusBadge, fmtBRL, fmtDate } from '../utils/helpers';
import { useT } from '../i18n/I18nContext';

export default function Dashboard() {
  const t = useT();
  const [data,    setData]    = useState(null);
  const [alerts,  setAlerts]  = useState([]);
  const [fobAlerts, setFobAlerts] = useState([]);
  const [recent,  setRecent]  = useState([]);
  const [loadError, setLoadError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Antes, um erro aqui era engolido e a tela desenhava tudo zerado — o que
    // parecia banco vazio. Agora a falha é dita em voz alta.
    stats.get().then(d => { setData(d); setLoadError(false); }).catch(() => { setData(null); setLoadError(true); });
    alertsApi.get().then(setAlerts).catch(() => setAlerts([]));
    alertsApi.fob().then(setFobAlerts).catch(() => setFobAlerts([]));
    quotations.list({ limit: 6 }).then(r => setRecent(r?.rows || r || [])).catch(() => setRecent([]));
  }, []);

  const byStatus = data?.byStatus || [];
  const totalOrdens = data?.total || 0;
  const aprovadas = byStatus.find(s=>s.status==='Approved')?.n || 0;
  const emAberto  = byStatus.filter(s=>!['Approved','Rejected','Order Placed'].includes(s.status)).reduce((s,r)=>s+r.n,0);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Dashboard</div>
          <div className="page-subtitle">{t('dashboard.subtitle')}</div>
        </div>
        <button className="btn btn-primary" onClick={()=>navigate('/quotations?new=1')}>{t('dashboard.new')}</button>
      </div>

      {loadError && (
        <div style={{
          marginBottom: 20, padding: '12px 16px', borderRadius: 'var(--radius)',
          background: '#FEF2F2', border: '1px solid #FECACA', color: '#B91C1C',
          fontSize: 13, display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ fontSize: 18 }}>⚠️</span>
          <span>{t('dashboard.load_error')}</span>
          <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }}
            onClick={() => window.location.reload()}>🔄</button>
        </div>
      )}

      {/* Produção a cobrar: data FOB atrasada ou vencendo em até 7 dias */}
      {fobAlerts.length > 0 && (
        <div style={{marginBottom:24}} data-testid="fob-alerts">
          <div style={{fontSize:13,fontWeight:700,color:'#B91C1C',marginBottom:10}}>
            🏭 {fobAlerts.length} pedido{fobAlerts.length > 1 ? 's' : ''} com data FOB a cobrar do fornecedor
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {fobAlerts.map(a => {
              const late = a.status === 'late';
              return (
                <div key={a.quotation_id} onClick={()=>navigate(`/orders/${a.quotation_id}`)}
                  style={{display:'flex',alignItems:'center',gap:14,padding:'12px 16px',borderRadius:'var(--radius)',border:`1.5px solid ${late?'#FECACA':'#F6E05E'}`,background:late?'#FEF2F2':'#FFFFF0',cursor:'pointer'}}>
                  <span style={{fontSize:18}}>{late?'🔴':'⏰'}</span>
                  <div style={{flex:1}}>
                    <span style={{fontWeight:700,color:'var(--pantone)'}}>{a.reference}</span>
                    <span style={{color:'var(--gray-500)',marginLeft:8,fontSize:13}}>{a.supplier_name}</span>
                    {a.collection&&<span className="tag" style={{marginLeft:8}}>{a.collection}</span>}
                    {a.slip_days > 0 && <span style={{marginLeft:8,fontSize:11,color:'#B45309'}}>já adiou {a.slip_days} dias</span>}
                  </div>
                  <div style={{textAlign:'right'}}>
                    <div style={{fontWeight:700,color:late?'#B91C1C':'#B7791F',fontFamily:'var(--font-mono)'}}>
                      {late ? `${a.days_late} dia${a.days_late>1?'s':''} de atraso` : a.days_remaining === 0 ? 'vence hoje' : `faltam ${a.days_remaining} dia${a.days_remaining>1?'s':''}`}
                    </div>
                    <div style={{fontSize:11,color:'var(--gray-500)'}}>FOB {String(a.date).split('-').reverse().join('/')}{late ? ' — cobrar' : ' — pedir status/fotos'}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {alerts.length > 0 && (
        <div style={{marginBottom:24}}>
          <div style={{fontSize:13,fontWeight:700,color:'var(--red)',marginBottom:10}}>⚠️ {alerts.length} {t('dashboard.alerts_msg')}</div>
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {alerts.map(a=>(
              <div key={a.quotation_id} onClick={()=>navigate(`/quotations/${a.quotation_id}`)}
                style={{display:'flex',alignItems:'center',gap:14,padding:'12px 16px',borderRadius:'var(--radius)',border:`1.5px solid ${a.severity==='critical'?'var(--red)':'var(--accent)'}`,background:a.severity==='critical'?'var(--red-light)':'var(--accent-light)',cursor:'pointer'}}>
                <span style={{fontSize:18}}>{a.severity==='critical'?'🔴':'🟡'}</span>
                <div style={{flex:1}}>
                  <span style={{fontWeight:700,color:'var(--pantone)'}}>{a.reference}</span>
                  <span style={{color:'var(--gray-500)',marginLeft:8,fontSize:13}}>{a.supplier_name}</span>
                  {a.collection&&<span className="tag" style={{marginLeft:8}}>{a.collection}</span>}
                </div>
                <div style={{textAlign:'right'}}>
                  <div style={{fontWeight:700,color:a.severity==='critical'?'var(--red)':'var(--accent)',fontFamily:'var(--font-mono)'}}>{a.days_stuck} dias</div>
                  <div style={{fontSize:11,color:'var(--gray-500)'}}>em "{a.status}" (limite: {a.threshold}d)</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="kpi-grid">
        <div className="kpi-card"><div className="kpi-label">{t('dashboard.kpi.total_orders')}</div><div className="kpi-value">{totalOrdens}</div></div>
        <div className="kpi-card"><div className="kpi-label">{t('dashboard.kpi.open')}</div><div className="kpi-value" style={{color:'var(--blue)'}}>{emAberto}</div></div>
        <div className="kpi-card"><div className="kpi-label">{t('dashboard.kpi.approved')}</div><div className="kpi-value" style={{color:'var(--green)'}}>{aprovadas}</div></div>
        <div className="kpi-card"><div className="kpi-label">{t('dashboard.kpi.alerts')}</div><div className="kpi-value" style={{color:alerts.length?'var(--red)':'var(--green)'}}>{alerts.length}</div><div className="kpi-sub">{t('dashboard.kpi.alerts_sub')}</div></div>
        <div className="kpi-card"><div className="kpi-label">{t('dashboard.kpi.suppliers')}</div><div className="kpi-value">{data?.totalSuppliers||0}</div></div>
        <div className="kpi-card"><div className="kpi-label">{t('dashboard.kpi.total_cost')}</div><div className="kpi-value" style={{fontSize:18}}>{fmtBRL(data?.totalBrl||0)}</div></div>
      </div>

      <div className="card" style={{marginBottom:20}}>
        <div className="card-header"><div className="card-title">{t('dashboard.orders_status')}</div><button className="btn btn-ghost btn-sm" onClick={()=>navigate('/kanban')}>{t('dashboard.view_kanban')}</button></div>
        <div className="card-body">
          {byStatus.length===0 ? <div className="text-muted">{t('quotations.empty')}</div> : (
            <div style={{display:'flex',flexWrap:'wrap',gap:10}}>
              {byStatus.map(s=>(
                <div key={s.status} style={{display:'flex',alignItems:'center',gap:10,background:'var(--gray-50)',border:'1px solid var(--gray-200)',borderRadius:'var(--radius)',padding:'10px 16px'}}>
                  <StatusBadge status={s.status} />
                  <span style={{fontFamily:'var(--font-mono)',fontWeight:700,fontSize:18,color:'var(--navy)'}}>{s.n}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header"><div className="card-title">{t('dashboard.recent')}</div><button className="btn btn-ghost btn-sm" onClick={()=>navigate('/quotations')}>{t('dashboard.view_all')}</button></div>
        <div className="card-body" style={{padding:0}}>
          {recent.length===0 ? (
            <div className="empty-state"><div className="empty-icon">📋</div><div className="empty-title">{t('quotations.empty')}</div></div>
          ) : (
            <div className="table-wrap" style={{border:'none',borderRadius:0}}>
              <table>
                <thead><tr>
                  <th></th>
                  <th>{t('col.reference')}</th>
                  <th>{t('col.supplier')}</th>
                  <th>{t('col.collection')}</th>
                  <th>{t('col.items')}</th>
                  <th>{t('col.est_cost_brl')}</th>
                  <th>{t('col.status')}</th>
                  <th>{t('col.created_at')}</th>
                </tr></thead>
                <tbody>
                  {recent.map(q=>(
                    <tr key={q.id} onClick={()=>navigate(`/quotations/${q.id}`)}>
                      <td style={{width:24}}>{q.alert ? (q.alert.severity==='critical'?'🔴':'🟡') : ''}</td>
                      <td className="mono">{q.reference}</td>
                      <td className="bold">{q.supplier_name}</td>
                      <td><span className="tag">{q.collection||'—'}</span></td>
                      <td>{q.item_count}</td>
                      <td className="text-green">{fmtBRL(q.total_estimated_brl)}</td>
                      <td><StatusBadge status={q.status} /></td>
                      <td className="text-muted">{fmtDate(q.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
